import { router } from "@inertiajs/react"
import { BackofficeLayout } from "@/components/layout/BackofficeLayout"
import { DetailGrid } from "@/components/backoffice/DetailGrid"
import { StageBadge } from "@/components/shared/StageBadge"
import { PaymentSummary } from "@/components/shared/PaymentSummary"
import { DocumentSection } from "@/components/shared/DocumentSection"
import { CalendarEventsSection } from "@/components/shared/CalendarEventsSection"
import { Button } from "@/components/ui/button"
import { useConfirm } from "@/components/ui/confirm-dialog"
import { CheckCircle, XCircle } from "lucide-react"
import { formatDate } from "@/lib/utils"

type Document = {
  id: string
  name: string
  file_url: string | null
  created_at: string
}

type Payment = {
  id: string
  service_fee_cents: number
  gov_fee_cents: number
  total_cents: number
  currency: string
  paid: boolean
  paid_at: string | null
  invoice_number: string | null
  receipt_number: string | null
  refunded: boolean
  refund_amount_cents: number
}

type CalendarEvent = {
  id: string
  title: string
  due_date: string
  source: string
  completed: boolean
  complete_path?: string
  delete_path?: string
}

type RegisteredAgentDetailed = {
  id: string
  stage: string
  type_name: string
  type_code: string
  jurisdiction_label: string
  business_name: string
  account_name: string
  duration_months: number
  created_at: string
  activated_at: string | null
  ended_at: string | null
  payment: Payment | null
  application_documents: Document[]
  active_documents: Document[]
}

type TypeSummary = {
  id: string
  code: string
  name: string
  jurisdiction_code: string
  jurisdiction_label: string
  gov_fee_cents: number
  service_fee_cents: number
  min_duration_months: number
}

type Props = {
  registered_agent: RegisteredAgentDetailed
  type: TypeSummary
  account: { id: string; name: string }
  templates: { id: string; name: string; file_url: string | null }[]
  calendar_events: CalendarEvent[]
  create_event_path: string | null
}

export default function BackofficeRegisteredAgentsShow({
  registered_agent,
  type,
  account,
  templates,
  calendar_events,
  create_event_path,
}: Props) {
  const confirm = useConfirm()

  const handleActivate = async () => {
    if (
      !(await confirm({
        title: "Activate Service",
        description: "Activate this registered agent service?",
        confirmLabel: "Activate",
      }))
    )
      return
    router.post(`/backoffice/registered_agents/${registered_agent.id}/activation`)
  }

  const handleTerminate = async () => {
    if (
      !(await confirm({
        title: "Terminate Service",
        description: "Terminate this registered agent service? This cannot be undone.",
        confirmLabel: "Terminate",
        destructive: true,
      }))
    )
      return
    router.post(`/backoffice/registered_agents/${registered_agent.id}/termination`)
  }

  return (
    <BackofficeLayout
      title={registered_agent.business_name}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        { label: "Registered Agents", href: "/backoffice/registered_agents" },
        { label: registered_agent.business_name },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">
              {registered_agent.business_name}
            </h1>
            <StageBadge stage={registered_agent.stage} variant="service" />
          </div>
          <p className="text-sm text-muted-foreground">
            {type.name} · {account.name}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {registered_agent.stage === "payment" &&
            registered_agent.payment?.paid && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleActivate}
                className="h-8 text-[13px]"
              >
                <CheckCircle className="mr-1.5 h-3 w-3" />
                Activate Service
              </Button>
            )}
          {registered_agent.stage === "active" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleTerminate}
              className="h-8 text-[13px] text-destructive hover:text-destructive"
            >
              <XCircle className="mr-1.5 h-3 w-3" />
              Terminate Service
            </Button>
          )}
        </div>

        <DetailGrid
          items={[
            { label: "Type", value: type.name },
            {
              label: "Jurisdiction",
              value: type.jurisdiction_label || type.jurisdiction_code || "—",
            },
            {
              label: "Duration",
              value: `${registered_agent.duration_months} months`,
            },
            { label: "Account", value: account.name },
            {
              label: "Created",
              value: formatDate(registered_agent.created_at),
            },
            {
              label: "Activated",
              value: registered_agent.activated_at
                ? formatDate(registered_agent.activated_at)
                : "—",
            },
            {
              label: "Ended",
              value: registered_agent.ended_at
                ? formatDate(registered_agent.ended_at)
                : "—",
            },
          ]}
        />

        {/* Payment */}
        <PaymentSummary
          payment={registered_agent.payment}
          adminActions={
            registered_agent.payment
              ? {
                  invoicePath: `/backoffice/registered_agents/${registered_agent.id}/payments/${registered_agent.payment.id}/invoice`,
                  confirmPaymentPath: `/backoffice/registered_agents/${registered_agent.id}/payments/${registered_agent.payment.id}/payment_confirmation`,
                  refundPath: `/backoffice/registered_agents/${registered_agent.id}/payments/${registered_agent.payment.id}/refund/new`,
                }
              : undefined
          }
        />

        {/* Documents */}
        <DocumentSection
          categories={[
            {
              label: "Application Documents",
              documents: registered_agent.application_documents || [],
              uploadPath: `/backoffice/registered_agents/${registered_agent.id}/application_documents`,
              deletePath: `/backoffice/registered_agents/${registered_agent.id}/application_documents`,
            },
            {
              label: "Active Documents",
              documents: registered_agent.active_documents || [],
              uploadPath: `/backoffice/registered_agents/${registered_agent.id}/active_documents`,
              deletePath: `/backoffice/registered_agents/${registered_agent.id}/active_documents`,
            },
          ]}
          templates={templates}
        />

        {/* Calendar Events */}
        <CalendarEventsSection
          events={calendar_events || []}
          createPath={create_event_path || undefined}
        />
      </div>
    </BackofficeLayout>
  )
}
